import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { Company } from 'src/app/generated/CrossPoint.IdentityManagement.Server';
import { CompanyType, DeviceSubscriptionOfferExt, SubscriptionState } from 'src/app/generated/CrossPoint.License.Server';
import { setCompanyAction } from 'src/app/store/actions/identitymanagement.actions';
import { getActiveLicensesInfoAction, getExpiredLicensesInfoAction, getExpiringLicensesInfoAction, LicensesActionsTypes } from 'src/app/store/actions/licenses.actions';
import { selectCompany } from 'src/app/store/selectors/identitymanagment.selector';
import { selectActiveStoreLicensesInfo, selectExpiredStoreLicensesInfo, selectExpiringStoreLicensesInfo } from 'src/app/store/selectors/licenses.selectors';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { LicenseFormatterService } from '../../services/license-formatter.service';
import { ShoppingBasketService } from '../../services/shopping-basket.service';
import { CompanyFilter, CompanyFilterComponent } from '../../../../shared/components/company-filter/company-filter.component';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { emptyCompany } from 'src/app/utils/functions/company.functions';

@UntilDestroy()
@Component({
  selector: 'xpac-licenses-list-tab',
  templateUrl: './licenses-list-tab.component.html',
  styleUrls: ['./licenses-list-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LicensesListTabComponent implements OnInit {
  private static readonly DEFAULT_NR_OF_LICENSES_TO_RETRIEVE: number = 100;
  private static readonly SEARCH_DEBOUNCE_TIME_MS: number = 500;

  @Input() public title: string = 'TITLE';
  @Input() public expired: boolean = false;
  @Input() public expiring: boolean = false;
  @Input() public isCrossPoint: boolean = false;

  public isLoading: boolean = true;
  public searchText: string = '';
  public storeLicenses: DeviceSubscriptionOfferExt[];
  public selectedCompany: Company = emptyCompany();
  public searchTimeoutHandle: number | null = null;
  public isLoadingFilteredLicenses: boolean = false;
  public companyFormGroup: UntypedFormGroup = null;

  private shoppingBasketService: ShoppingBasketService = null;

  constructor(
    private licenseFormatterService: LicenseFormatterService,
    private injector:Injector,
    private store: Store<IRemoteXPACPortalState>,
    private datePipe: LocalizedDatePipe,
    private cdr: ChangeDetectorRef,
    private translatePipe: TranslatePipe,
    private actionsListener$: Actions,
    private translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    public dialog: DialogAnimationService,
  ) { }

  public ngOnInit(): void {
    this.initForms();
    this.initListeners();
    this.initData();
  }

  public getFormattedLicenseTitle(license: DeviceSubscriptionOfferExt): string {
    return this.licenseFormatterService.getFormattedLicenseTitle(license);
  }

  public getFormattedLicenseSubTitle(license: DeviceSubscriptionOfferExt): string {
    return this.licenseFormatterService.getFormattedLicenseSubTitle(license);
  }

  public getLicenseInformation(license: DeviceSubscriptionOfferExt): string {
    if (license.remoteMaintenanceEndsUtc) {
      if(new Date() < new Date(license.remoteMaintenanceEndsUtc)){
        return this.translatePipe.transform('Modules.Licenses.List.Expired.Expires') + " " + this.datePipe.transform(license.remoteMaintenanceEndsUtc, 'shortDate');
      }
      return this.translatePipe.transform('Modules.Licenses.List.Expired.Expired') + " " + this.datePipe.transform(license.remoteMaintenanceEndsUtc, 'shortDate');
    } 
    else if(license.remoteMaintenanceEndsUtc === null && license.months === 0){
      const remoteMaintenanceStartsUtc: Date = new Date(license.remoteMaintenanceStartsUtc);
      const eightMonthsFromRemoteMaintenanceStartsUtc: Date  = new Date(remoteMaintenanceStartsUtc.setMonth(remoteMaintenanceStartsUtc.getMonth()+6));
      return this.translatePipe.transform('Modules.Licenses.List.AutoRenewal') + " " + this.datePipe.transform(eightMonthsFromRemoteMaintenanceStartsUtc.toUTCString(), 'shortDate');
    }
    else {
      return this.translatePipe.transform('Modules.Licenses.List.NoLicense');
    }
  }

  public openCompaniesFilterDialog(): void {
    let headerText: string = this.translate.instant('Modules.Licenses.List.FilteredCompaniesHeader');

    const dialogRef = this.dialog.open(CompanyFilterComponent, {
      panelClass: 'my-full-screen-dialog',
      data: {header: headerText, companyType: CompanyType.Dealer},
      animation: { to: "left" }
    });
    dialogRef.afterClosed().subscribe((company: CompanyFilter) => {
      if (company.selectedCompany) {
        this.store.dispatch(setCompanyAction({ data: company.selectedCompany }));        
      }
      this.cdr.markForCheck();
    });
  }

  private onSelectedCompanyChanged(company: Company): void {
    this.selectedCompany = company;
    this.companyFormGroup.patchValue({
      selectedCompany: this.getDescription(),
    });
    this.cdr.markForCheck();
  }

  public getDescription(): string {
    return this.selectedCompany.description !== "" ? this.selectedCompany.description : this.selectedCompany.companyId;
  } 

  public isInBasket(license: DeviceSubscriptionOfferExt): boolean {
    if (this.isCrossPoint) {
      return false;
    }
    return this.shoppingBasketService.isLicenseInShoppingBasket(license);
  }

  public showLicenses(): boolean{
    if(this.isCrossPoint){
      if(this.selectedCompany.companyId === ''){
        return false;
      }
      else{
        return true;
      }
    }
    return  true;
  }

  public onLicenseClicked(license: DeviceSubscriptionOfferExt): void {
    if (!this.shoppingBasketService) {
      return;
    }

    const wasSelected: boolean = this.isInBasket(license);
    if (wasSelected) {
      this.shoppingBasketService.remove(license);
    }
    else {
      this.shoppingBasketService.add(license);
    }

    this.cdr.markForCheck();
  }

  public async onSearchTextChanged(searchText: string): Promise<void> {
    this.searchText = searchText;

    if (this.searchTimeoutHandle !== null) {
      clearTimeout(this.searchTimeoutHandle);
    }

    while (this.isLoadingFilteredLicenses) {
      // Wait a little so an previous call can be dispatched first.
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isLoadingFilteredLicenses = true;
    this.cdr.markForCheck();

    // Wait some time before requesting
    this.searchTimeoutHandle = window.setTimeout(() => {
      this.retrieveLicenses(searchText);
    }, LicensesListTabComponent.SEARCH_DEBOUNCE_TIME_MS);
  }

  private initData(): void {
    if(!this.isCrossPoint){
      this.retrieveLicenses();
    }
  }

  private retrieveLicenses(search?: string, maxItems: number = LicensesListTabComponent.DEFAULT_NR_OF_LICENSES_TO_RETRIEVE): void {
    if (this.expired) {
      // Get last expired store licenses
      this.store.dispatch(getExpiredLicensesInfoAction({ data: { subscriptionState: SubscriptionState.Expired, dealerId: this.selectedCompany.companyId, search: search, maxItems: maxItems } }));
    }
    else {

      if (this.expiring) {
        // Get last expiring store licenses
        this.store.dispatch(getExpiringLicensesInfoAction({ data: { subscriptionState: SubscriptionState.Expiring, dealerId: this.selectedCompany.companyId, search: search, maxItems: maxItems } }));
      }
      else {
        // Get last active store licenses
        this.store.dispatch(getActiveLicensesInfoAction({ data: { subscriptionState: SubscriptionState.Active, dealerId: this.selectedCompany.companyId, search: search, maxItems: maxItems } }));
      }
    }
  }

  private initForms(): void {
    this.companyFormGroup = this.formBuilder.group({
      selectedCompany: [{ value: '', disabled: false }],    
    });
  }

  private initListeners(): void {

    if (!this.isCrossPoint) {
      this.shoppingBasketService = this.injector.get(ShoppingBasketService);
      this.shoppingBasketService.onLicensesChangedCallback.subscribe((data: any) => {
        this.cdr.markForCheck();
      });

      this.actionsListener$
        .pipe(ofType(LicensesActionsTypes.CREATE_LICENSE_ORDER_SUCCESS))
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.retrieveLicenses(this.searchText);
        });
    } else {
      // Select the company if set
      this.store.select(selectCompany)
        .pipe(
          filter<Company>(Boolean),
          untilDestroyed(this)
        )
        .subscribe((selectedCompany: Company) => {
          this.onSelectedCompanyChanged(selectedCompany);
          this.retrieveLicenses();
        });

      this.isLoading = false;
      this.cdr.markForCheck();
    }

    if (this.expired) {
      this.store.select(selectExpiredStoreLicensesInfo)
        .pipe(
          filter<DeviceSubscriptionOfferExt[]>(Boolean),
          untilDestroyed(this)
        )
        .subscribe((licenses: DeviceSubscriptionOfferExt[]) => {
          this.storeLicenses = licenses;
          this.isLoading = false;
          this.isLoadingFilteredLicenses = false;
          this.cdr.markForCheck();
        });
    }
    else {
      if (this.expiring) {
        this.store.select(selectExpiringStoreLicensesInfo)
          .pipe(
            filter<DeviceSubscriptionOfferExt[]>(Boolean),
            untilDestroyed(this)
          )
          .subscribe((licenses: DeviceSubscriptionOfferExt[]) => {
            this.storeLicenses = licenses;
            this.isLoading = false;
            this.isLoadingFilteredLicenses = false;
            this.cdr.markForCheck();
          });
      }
      else {
        this.store.select(selectActiveStoreLicensesInfo)
          .pipe(
            filter<DeviceSubscriptionOfferExt[]>(Boolean),
            untilDestroyed(this)
          )
          .subscribe((licenses: DeviceSubscriptionOfferExt[]) => {
            this.storeLicenses = licenses;
            this.isLoading = false;
            this.isLoadingFilteredLicenses = false;
            this.cdr.markForCheck();
          });
      }
    }
  }
}
